import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStore,
  faArrowLeft,
  faSpinner,
  faInfoCircle,
  faUserTie,
  faMoneyBillWave,
  faCoins,
  faCalculator,
} from "@fortawesome/free-solid-svg-icons";
import ShopInfoTab from "./ShopInfoTab";
import CapitalTab from "./CapitalTab";
import OwnerTab from "./OwnerTab";
import FinancialTab from "./FinancialTab";
import SalesCloseoutTab from "./SalesCloseoutTab";
import { useAuth, useShop, useCapitals } from "../../../hooks";
import { useTransactions } from "../../../hooks/useTransactions";

const ShopDetails = () => {
  const navigate = useNavigate();
  const { shopId } = useParams();
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState("info");

  const { shop: responseShop, loading, error, refresh } = useShop(shopId);

  const { transactions } = useTransactions(shopId, { per_page: 1000 });
  const { capitals } = useCapitals(shopId, { per_page: 1000 });

  const shop = responseShop && {
    ...responseShop,
    shopId: responseShop.shopId ?? responseShop.id ?? shopId,
    name: responseShop.name ?? responseShop.shop_name,
    type: responseShop.type ?? responseShop.shop_type,
  };

  const canViewCapital = hasPermission([
    "manage_shop_modules",
    "manage_capital",
    "read_capital",
  ]);
  const canViewOwners = hasPermission([
    "manage_shop_modules",
    "manage_business_owner",
    "read_business_owner",
  ]);
  const canViewFinancial = hasPermission([
    "manage_shop_modules",
    "manage_financial_records",
    "read_financial_records",
  ]);

  const canViewCloseouts =
    hasPermission([
      "manage_all",
      "manage_shop_modules",
      "manage_sales_closeout",
      "read_sales_closeout",
    ]) &&
    hasPermission([
      "manage_business_seller",
      "read_business_seller",
      "manage_user_account",
      "read_user_account",
    ]);

  const tabs = useMemo(
    () =>
      [
        {
          id: "info",
          label: "Shop details",
          icon: faInfoCircle,
          visible: true,
        },
        {
          id: "capital",
          label: "Capital details",
          icon: faCoins,
          visible: canViewCapital,
        },
        {
          id: "owner",
          label: "Owners details",
          icon: faUserTie,
          visible: canViewOwners,
        },
        {
          id: "financial",
          label: "Financial details",
          icon: faMoneyBillWave,
          visible: canViewFinancial,
        },
        {
          id: "salescloseout",
          label: "Sales Closeout",
          icon: faCalculator,
          visible: canViewCloseouts,
        },
      ].filter((t) => t.visible),
    [canViewCapital, canViewOwners, canViewFinancial, canViewCloseouts],
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <FontAwesomeIcon
          icon={faSpinner}
          className="h-8 w-8 text-blue-600 animate-spin"
        />
        <p className="mt-4 text-gray-600">Loading shop details...</p>
      </div>
    );
  }

  if (error || !shop) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-600 text-lg">Shop not found</div>
        <Button
          onClick={() => refresh()}
          className="mt-4 mr-2 bg-blue-800 hover:bg-blue-900 text-white"
        >
          Retry
        </Button>
        <Button
          onClick={() => navigate("/shop/shops")}
          className="mt-4 bg-blue-800 hover:bg-blue-900 text-white"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Shops
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 max-w-6xl mx-auto px-3 sm:px-4 lg:px-0">
      <Card className="bg-white border border-gray-200 px-3 sm:px-4 lg:px-6 py-3 sm:py-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-0 md:gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/shop/shops")}
              className="text-gray-600 hover:text-gray-700"
            >
              <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                <FontAwesomeIcon
                  icon={faStore}
                  className="text-blue-800 h-4 w-4 sm:h-5 sm:w-5"
                />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-900">
                  {shop.name}
                </h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-0.5 italic font-medium">
                  View shop informations
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-1 bg-gray-100 rounded-lg p-1 w-full">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center justify-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-md text-sm sm:text-md font-medium transition-all duration-200 ${
              activeTab === tab.id
                ? "bg-blue-800 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-200/50"
            }`}
          >
            <FontAwesomeIcon icon={tab.icon} className="h-3.5 w-3.5" />
            <span className="whitespace-nowrap">{tab.label}</span>
          </button>
        ))}
      </div>

      <Card className="bg-white border border-gray-200 shadow-sm p-4 sm:p-6">
        {activeTab === "info" && (
          <ShopInfoTab
            shop={shop}
            capitals={capitals}
            transactions={transactions}
          />
        )}
        {activeTab === "capital" && (
          <CapitalTab shopId={shop.shopId} capitals={capitals} />
        )}
        {activeTab === "owner" && <OwnerTab shop={shop} />}
        {activeTab === "financial" && (
          <FinancialTab shopId={shop.shopId} transactions={transactions} />
        )}
        {activeTab === "salescloseout" && <SalesCloseoutTab shop={shop} />}
      </Card>
    </div>
  );
};

export default ShopDetails;
